import { EventEmitter } from 'ahooks/lib/useEventEmitter';
import { useEffect, useState } from 'react';
import {
  VITE_AVAILABILITY_PROMPT_COOLDOWN_MS,
  VITE_AVAILABILITY_PROMPT_MAX_NUDGES,
} from 'src/constants/envs';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useUserAvailability } from 'src/hooks/availability/useUserAvailability.hook';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { eAvailabilityPromptReason } from './availabilityPrompt.enum';

/**
 * Module-scoped bus between "the seller just did something" and "ask them
 * about it". Deliberately NOT a provider: the hierarchy is already 15 deep and
 * `ModalsProvider` re-renders every consumer on any toggle, so a prompt that
 * fires on the chat send path has no business living there.
 *
 * It is also why the trigger sites stay dumb — they announce the action and
 * know nothing about availability, keeping `useUserAvailability` (and its
 * `workspaceInfo` subscription) out of the message-send hot path. Every
 * decision about whether to interrupt lives in `useAvailabilityPrompt` below,
 * so there is exactly one place to reason about the policy.
 */
const availabilityPromptEvent = new EventEmitter<eAvailabilityPromptReason>();

type tEpisode = {
  /** Workspace this episode belongs to; another workspace is its own episode. */
  workspaceId: string;
  /** How many times we have already interrupted during this away-episode. */
  nudges: number;
  /** `Date.now()` of the last interruption, for the cooldown. */
  lastShownAt: number;
};

/**
 * The current away-episode's nudge budget, or `null` when nothing is in flight.
 *
 * Module scope, not a ref: `PrivateLayout` unmounts on any trip through a
 * public/onboarding route, and a per-mount ref would hand the seller a fresh
 * budget every time they navigated. It does NOT survive a full page reload —
 * the module re-evaluates — which is why the budget is several nudges rather
 * than one: reloads are rare enough here that "we'll catch them next reload"
 * is not a real second chance.
 */
let episode: tEpisode | null = null;

/**
 * Announce an action that contradicts being Inactivo. Safe to call from
 * anywhere, at any time: with no surface mounted this is a no-op, and it never
 * throws into the caller's success path.
 *
 * The try/catch is what makes that last guarantee real rather than incidental.
 * `EventEmitter.emit` re-throws whatever a subscriber raises, and every call
 * site here sits inside the caller's `try` AFTER the send or lead move already
 * succeeded — so an exception escaping this function would trip
 * `catch (error) { addAlert(...) }` and tell the seller their message failed
 * when it had in fact been delivered. A prompt is never worth a false failure.
 */
export function requestAvailabilityPrompt(
  reason: eAvailabilityPromptReason,
): void {
  try {
    availabilityPromptEvent.emit(reason);
  } catch (error) {
    LLog.error(
      {
        component: LOG_COMPONENTS.UI,
        method: 'requestAvailabilityPrompt',
        operation: LOG_OPERATIONS.ERROR,
      },
      'Availability prompt subscriber threw',
      error instanceof Error ? error : new Error(String(error)),
      { reason },
    );
  }
}

/** Test seam: clears the module-scoped episode budget between cases. */
export function resetAvailabilityPromptEpisode(): void {
  episode = null;
}

export type tAvailabilityPrompt = {
  /** The reason the prompt is open for, or `null` while closed. */
  reason: eAvailabilityPromptReason | null;
  /** Flip back to Activo and close. */
  goActive: () => Promise<void>;
  /** Close this nudge and stay Inactivo. Does not end the episode. */
  stayInactive: () => void;
};

/**
 * Owns the "should we interrupt?" policy for the availability prompt.
 *
 * Asks at most `VITE_AVAILABILITY_PROMPT_MAX_NUDGES` times per away-episode,
 * spaced by `VITE_AVAILABILITY_PROMPT_COOLDOWN_MS`, then stays quiet until the
 * seller returns to Activo. A single ask was the original design and it was
 * too weak in practice: reloads are rare here, so one dismissal — including an
 * accidental click outside the dialog — bought silence for the rest of what can
 * be a 28-hour episode, which is the exact failure this feature exists to
 * catch. Choosing "volver a activo" does NOT end the episode by itself:
 * the resulting Activo state is what stops further prompts, so if that write
 * fails — or no-ops behind `useUserAvailability`'s in-flight guard — the seller
 * is still Inactivo, still unsilenced, and the next action asks again. That
 * asymmetry is deliberate: a failed recovery is precisely the state this
 * feature exists to catch, and it must not be the one outcome that goes quiet.
 *
 * The ceiling is also what lets the dialog omit a "don't ask again" control:
 * the shipped `InactiveUserBanner` is non-dismissible precisely because a
 * permanent opt-out recreates the silent-inactive week the ticket exists to
 * prevent, and a prompt this cheap to decline never needs one.
 */
export function useAvailabilityPrompt(): tAvailabilityPrompt {
  const { trackEvent } = useAnalytics();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { isActive, isLoaded, setAvailability } = useUserAvailability();
  const [reason, setReason] = useState<eAvailabilityPromptReason | null>(null);

  // Normalised once, so every comparison below is against the same value. An
  // earlier version applied `?? ''` at the write sites but not in the re-arm
  // effect, so a workspace id that resolved late stored `''` and then never
  // matched `undefined` — the episode could never be cleared.
  const workspaceId = currentWorkspaceId ?? '';

  // Re-arm for the next away-episode. Runs on every return to Activo,
  // including the one this prompt just caused, so the seller who goes away
  // again tomorrow gets a full budget.
  useEffect(() => {
    if (isActive && episode?.workspaceId === workspaceId) {
      episode = null;
    }
  }, [isActive, workspaceId]);

  availabilityPromptEvent.useSubscription((next) => {
    // `isActive` derives from `!currentUserInfo?.isInactive`, which reads
    // "active" before `workspaceInfo` resolves — gate on `isLoaded` or the
    // prompt can never fire for a seller whose first action beats the fetch.
    if (!isLoaded || isActive) return;
    // Already asking — never stack a second dialog over the open one.
    if (reason) return;

    const current =
      episode?.workspaceId === workspaceId
        ? episode
        : { workspaceId, nudges: 0, lastShownAt: 0 };

    // Spent this episode's budget. Only returning to Activo re-arms it.
    if (current.nudges >= VITE_AVAILABILITY_PROMPT_MAX_NUDGES) return;
    // Inside the quiet window. Counting on *show* rather than on dismissal is
    // what makes this safe: an accidental backdrop click costs one nudge, not
    // the whole episode, and a burst of sends cannot spend the budget at once.
    if (
      current.nudges > 0 &&
      Date.now() - current.lastShownAt < VITE_AVAILABILITY_PROMPT_COOLDOWN_MS
    ) {
      return;
    }

    episode = {
      workspaceId,
      nudges: current.nudges + 1,
      lastShownAt: Date.now(),
    };
    setReason(next);
    trackEvent('front.availability_prompt.shown', {
      reason: next,
      nudge: episode.nudges,
    });
  });

  async function goActive(): Promise<void> {
    trackEvent('front.availability_prompt.button.click_back_to_active', {
      reason,
    });
    // Close first, deliberately, and note what that rules out: because this
    // and `setAvailability`'s own in-flight flag land in the same React batch,
    // the dialog is already unmounted before any "saving" state could paint —
    // so this surface has no spinner or disabled state, and must not grow one.
    // The click reads as instant; `setAvailability` raises its own error toast
    // if the write fails, and the still-mounted banner keeps the seller's real
    // state honest underneath. Double-clicks cannot reach the second call: the
    // dialog is gone, and `availabilityUpdateInFlight` guards the write anyway.
    setReason(null);
    // Clear the quiet window — but not the spent nudge. The seller did the
    // right thing, so if the write silently fails (or no-ops behind the
    // in-flight guard) the next action re-asks immediately instead of leaving
    // them wrongly Inactivo for a whole cooldown. Keeping the nudge spent is
    // what stops a permanently failing write from prompting on every message:
    // it still stops at the configured budget.
    if (episode?.workspaceId === workspaceId) {
      episode = { ...episode, lastShownAt: 0 };
    }
    await setAvailability(true);
  }

  function stayInactive(): void {
    trackEvent('front.availability_prompt.button.click_stay_inactive', {
      reason,
    });
    // Deliberately does NOT silence the rest of the episode: the budget is
    // spent on show, so declining just closes this one and the cooldown keeps
    // the next nudge from arriving on the very next message.
    setReason(null);
  }

  return { reason, goActive, stayInactive };
}
